/**
 * Referral Tracker (formerly "Ghost Report").
 *
 * PRODUCT-FIRST / income-claim-free (Jul 2026, Randy): shows WHO you've shared
 * My Silo with and where they are in the journey — NO projected dollar figures,
 * no "what you'd earn", no recruitment framing. Any Partner Program mention
 * carries the earnings disclaimer.
 *
 * Reads ONLY from /api/me/ghost-report (no side effects).
 * Contract unchanged: { shareUrl, variant }.
 */
import { useEffect, useState } from "react";
import { Users, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import ShareMySiloButton from "@/components/ShareMySiloButton";

// Nov 2026 (Randy) — same pill as the marketing page's share button, in the
// card that talks about sharing.
const SHARE_PILL =
  "inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4A5D4E]/35 bg-white text-[#3A4A3E] text-xs hover:bg-[#F0F1EE] hover:border-[#4A5D4E] transition-colors max-w-full";

export default function GhostReport({ shareUrl, variant = "creator", showShareButton = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFan = variant === "fan";

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/ghost-report");
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || "Could not load your referrals.");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="text-[#6B6A68] text-sm py-4" data-testid="ghost-report-loading">Loading referrals…</div>;
  if (!data || !data.enabled) return null;                 // Super Admin killed it globally
  if (data.is_already_affiliate) return null;              // Pro members see a different surface

  const rows = data.referrals_in_window || [];

  // EMPTY — nobody in the ripple yet.
  if (!data.total_pending_count) {
    return (
      <div className="bg-white border hairline rounded-xl p-6" data-testid="ghost-report-empty">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
            <Users size={20} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif-display text-xl text-[#1C1B1A]">People you've shared with</h3>
            <p className="text-sm text-[#6B6A68] mt-1">
              When you share My Silo with someone through your personal link, we quietly track who's exploring it —
              so you can see your ripple grow.
            </p>
            {shareUrl && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[#6B6A68]">Your share link:</span>
                <code className="text-xs bg-[#F0F1EE] px-2 py-1 rounded font-mono" data-testid="ghost-report-share-url">{shareUrl}</code>
              </div>
            )}
            {showShareButton && (
              <div className="mt-4">
                <ShareMySiloButton className={SHARE_PILL} testId="ghost-report-share-btn" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // POPULATED — neutral counts + the referee list. No dollar projections.
  return (
    <div className="rounded-xl p-6 border bg-[#E8F0E9] border-[#4A5D4E]/30" data-testid="ghost-report-active">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 bg-[#4A5D4E] text-white">
          <Users size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]">People you've shared with</h3>
          <p className="text-sm text-[#1C1B1A] mt-2 leading-relaxed" data-testid="ghost-report-headline">
            <strong className="text-[#1C1B1A]">{data.total_pending_count} {data.total_pending_count === 1 ? "person" : "people"}</strong>{" "}
            you shared with {data.total_pending_count === 1 ? "is" : "are"} exploring My Silo.
            {data.paid_referee_count > 0 && (
              <> <strong className="text-[#4A5D4E]">{data.paid_referee_count}</strong> {data.paid_referee_count === 1 ? "has" : "have"} become paying creators.</>
            )}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4" data-testid="ghost-report-stats">
            <Stat icon={<Users size={14} weight="duotone" />} label="Exploring" value={data.total_pending_count} />
            <Stat icon={<Sparkle size={14} weight="duotone" />} label="Paying creators" value={data.paid_referee_count} />
            <Stat icon={<Sparkle size={14} weight="duotone" />} label="Creator Pro" value={data.affiliate_referee_count || 0} />
          </div>

          {rows.length > 0 && (
            <div className="mt-5 space-y-2" data-testid="ghost-report-referee-list">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-1">Who's in your ripple</div>
              {rows.map((r) => <RefereeRow key={r.invitee_email + r.stage} row={r} />)}
            </div>
          )}
        </div>
      </div>

      {shareUrl && (
        <div className="mt-4 pt-4 border-t border-[#4A5D4E]/20 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#6B6A68]">Your share link:</span>
          <code className="text-xs bg-white/70 px-2 py-1 rounded font-mono" data-testid="ghost-report-share-url">{shareUrl}</code>
        </div>
      )}
      {showShareButton && (
        <div className="mt-4 pt-4 border-t border-[#4A5D4E]/20">
          <ShareMySiloButton className={SHARE_PILL} testId="ghost-report-share-btn" />
        </div>
      )}

      <div className="mt-4 flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-[#4A5D4E]/20">
        <p className="text-xs text-[#6B6A68] flex-1 min-w-[220px]">
          {isFan
            ? "Become a Creator, then upgrade to Creator Pro to unlock the full toolkit."
            : "Upgrade to Creator Pro to unlock the full My Silo toolkit."}
        </p>
        <button
          type="button"
          data-testid="ghost-report-upgrade-cta"
          onClick={() => toast(isFan
            ? "Step 1: become a Creator. Step 2: upgrade to Creator Pro for the full toolkit."
            : "Head to Earnings → Creator Pro to unlock the full toolkit.")}
          className="px-4 py-2 rounded-md bg-[#1C1B1A] text-white text-sm hover:bg-[#3A4A3E] flex items-center gap-1.5"
        >
          Learn about Creator Pro <ArrowRight size={14} weight="duotone" />
        </button>
      </div>

      <div className="mt-3">
        <EarningsDisclaimer variant="short" />
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-md px-3 py-2 bg-white/80">
      <div className="text-[10px] uppercase tracking-[0.18em] flex items-center gap-1 text-[#6B6A68]">{icon} {label}</div>
      <div className="font-serif-display text-lg tabular-nums text-[#1C1B1A]">{value}</div>
    </div>
  );
}

// Per-referee row — stage + name only. No projected earnings.
function RefereeRow({ row }) {
  const stageMeta = {
    affiliate:  { label: "Creator Pro ★", bg: "bg-[#2F8F4E]",  fg: "text-white" },
    creator:    { label: "Paid creator", bg: "bg-[#4A5D4E]", fg: "text-white" },
    // Oct 2026 (Randy, seeding pass) — a referee mid-trial or on a comp used to
    // read "Signed up" forever, which under-reports what actually happened:
    // they're in and using the platform. Neither is money yet, so neither is
    // dressed up as a paid creator.
    trialing:   { label: "On trial", bg: "bg-[#C9922F]", fg: "text-white" },
    comped:     { label: "Comped", bg: "bg-[#6B6A68]", fg: "text-white" },
    signed_up:  { label: "Signed up",   bg: "bg-[#A89360]",  fg: "text-white" },
    clicked:    { label: "Clicked link", bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]" },
  }[row.stage] || { label: row.stage || "—", bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]" };
  return (
    <div className="rounded-md p-3 flex items-center gap-3 flex-wrap bg-white/70 border hairline" data-testid={`ghost-referee-row-${row.stage}`}>
      <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded ${stageMeta.bg} ${stageMeta.fg}`}>
        {stageMeta.label}
      </span>
      <div className="flex-1 min-w-[140px] text-sm text-[#1C1B1A] truncate">
        {row.display_label}
      </div>
    </div>
  );
}
